import React, { useState } from 'react';
import { Lock } from 'lucide-react';

// Generic password-gate overlay. Pass a `verify` function that takes the
// entered password and resolves to a boolean (whether it was correct).
const PasswordGate = ({ title, subtitle, verify, onUnlocked, onCancel }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!password.trim()) return;
    setLoading(true);
    setError('');
    try {
      const valid = await verify(password);
      if (valid) {
        onUnlocked();
      } else {
        setError('Incorrect password. Please try again.');
        setPassword('');
      }
    } catch {
      setError('Error verifying password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm mx-4 text-center">
        <div className="w-14 h-14 bg-[#c0c0e6] rounded-full flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-[#01006A]" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-500 mb-5">{subtitle || 'This is password protected.'}</p>
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="Enter password"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A] mb-2 text-center"
          autoFocus
        />
        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
        <div className="flex gap-3 mt-3">
          <button onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading || !password.trim()}
            className="flex-1 px-4 py-2 bg-[#01006A] text-white rounded-lg hover:bg-[#010059] disabled:bg-gray-300 text-sm font-medium">
            {loading ? 'Checking…' : 'Unlock'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasswordGate;
